import React from 'react';
import { addDays, differenceInDays } from 'date-fns';
import CloudIcon from '@mui/icons-material/Cloud';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import StorageIcon from '@mui/icons-material/Storage';
import ClassIcon from '@mui/icons-material/Class';
import SecurityIcon from '@mui/icons-material/Security';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import FactoryIcon from '@mui/icons-material/Factory';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import BusinessIcon from '@mui/icons-material/Business';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import PolicyIcon from '@mui/icons-material/Policy';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import Looks3Icon from '@mui/icons-material/Looks3';
import PublicIcon from '@mui/icons-material/Public';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import { Box } from '@mui/material';
import { TIER_COLORS } from '../../../../Constants/Colors';
import { parseJsonArray } from '../../../../Utils/frameworkTiers';

// Component to render tier-based icons with appropriate colors
export const TierIcon = ({ tier }) => {
  const getTierColor = () => {
    switch(tier) {
      case 1: return TIER_COLORS.TIER1;
      case 2: return TIER_COLORS.TIER2;
      case 3: return TIER_COLORS.TIER3;
      case 4: return TIER_COLORS.TIER4;
      case 5: return TIER_COLORS.TIER5;
      default: return '#1976d2';
    }
  };
  
  return (
    <Box sx={{ position: 'relative' }}>
      <WorkspacePremiumIcon 
        sx={{ 
          color: getTierColor(),
          fontSize: '1.2rem',
        }}
      />
    </Box>
  );
};

export const CTM_TIERS = [
  {
    key: 'supporter',
    value: 'Supporter',
    tierNumber: 1,
    shortLabel: 'T1 - Supporter',
    formalLabel: 'Tier 1 - Supporter',
    description: 'Basic level of cybersecurity implementation',
  },
  {
    key: 'practitioner',
    value: 'Practitioner',
    tierNumber: 2,
    shortLabel: 'T2 - Practitioner',
    formalLabel: 'Tier 2 - Practitioner',
    description: 'Intermediate level with established security practices',
  },
  {
    key: 'promoter',
    value: 'Promoter',
    tierNumber: 3,
    shortLabel: 'T3 - Promoter',
    formalLabel: 'Tier 3 - Promoter',
    description: 'Advanced implementation with proactive security measures',
  },
  {
    key: 'performer',
    value: 'Performer',
    tierNumber: 4,
    shortLabel: 'T4 - Performer',
    formalLabel: 'Tier 4 - Performer',
    description: 'Comprehensive security program with mature processes',
  },
  {
    key: 'advocate',
    value: 'Advocate',
    tierNumber: 5,
    shortLabel: 'T5 - Advocate',
    formalLabel: 'Tier 5 - Advocate',
    description: 'Excellence in cybersecurity with industry-leading practices',
  },
];

export const CTM_TIER_OPTIONS = CTM_TIERS.map((tier) => ({
  value: tier.formalLabel,
  description: tier.description,
  icon: <TierIcon tier={tier.tierNumber} />,
}));

export const normalizeCtmTier = (tierValue) => {
  const value = String(tierValue || '').toLowerCase();
  if (!value) return null;

  const numberMatch = value.match(/\b(?:tier|t)\s*([1-5])\b/);
  if (numberMatch) {
    return CTM_TIERS.find((tier) => tier.tierNumber === Number(numberMatch[1])) || null;
  }

  return CTM_TIERS.find((tier) => value.includes(tier.value.toLowerCase())) || null;
};

export const getCtmTierPlainValue = (tierValue) => normalizeCtmTier(tierValue)?.value || tierValue;

export const getCtmTierShortLabel = (tierValue) => normalizeCtmTier(tierValue)?.shortLabel || tierValue;

export const getCtmTierFormalLabel = (tierValue) => normalizeCtmTier(tierValue)?.formalLabel || tierValue;

export const normalizeCtmCoverageValue = (coverageValue) => {
  const tier = normalizeCtmTier(coverageValue);
  return tier ? tier.formalLabel : coverageValue;
};

export const getCoverageShortLabel = (coverageValue, frameworkAcronym) => {
  return isCtmFramework(frameworkAcronym) ? getCtmTierShortLabel(coverageValue) : coverageValue;
};

export const isCtmFramework = (frameworkAcronym) => frameworkAcronym === 'CTM:2022' || frameworkAcronym === 'CTM:2025';

export const normalizeCoverageList = (coverage, frameworkAcronym) => {
  const coverageList = Array.isArray(coverage) ? coverage : [coverage].filter(Boolean);
  return isCtmFramework(frameworkAcronym) ? coverageList.map(normalizeCtmCoverageValue) : coverageList;
};

// Coverage options by framework code (name_acronym)
export const coverageOptionsByFramework = {
  "CEM:2022": [
    { value: "Default", description: "Standard implementation with focus on basic cybersecurity controls", icon: <ClassIcon /> },
    { value: "CMS Vendor", description: "CMS vendor management controls", icon: <BusinessIcon /> },
    { value: "Data Protection Essential (DPE) Combined", description: "Combined Data Protection Essential framework", icon: <ShieldIcon /> }
  ],
  "CEM:2025": [
    { value: "Classical Cybersecurity", description: "Default - Standard cybersecurity controls", icon: <ClassIcon /> },
    { value: "Cloud Security", description: "Controls for cloud environments and services", icon: <CloudIcon /> },
    { value: "OT Security", description: "Operational Technology security controls", icon: <FactoryIcon /> },
    { value: "AI Security", description: "Artificial Intelligence security controls", icon: <SmartToyIcon /> },
    { value: "ICT Vendor", description: "ICT vendor management controls", icon: <BusinessIcon /> },
    { value: "Data Protection Essential (DPE) Combined", description: "Combined Data Protection Essential framework", icon: <ShieldIcon /> }
  ],
  "ISO27001:2022": [
    { value: "Cloud Security", description: "Implementation focusing on cloud infrastructure and services", icon: <CloudIcon /> },
    { value: "Artificial intelligence (AI)", description: "Implementation with emphasis on AI systems and algorithms", icon: <SmartToyIcon /> },
    { value: "Application (iOS / Android)", description: "Mobile application security controls and implementation", icon: <PhoneIphoneIcon /> },
    { value: "On Premise", description: "Traditional on-premises infrastructure security implementation", icon: <StorageIcon /> }
  ],
  "CTM:2022": [
    ...CTM_TIER_OPTIONS
  ],
  "CTM:2025": [
    { value: "Classical Cybersecurity", description: "Default - Standard cybersecurity controls", icon: <ClassIcon /> },
    ...CTM_TIER_OPTIONS,
    { value: "Cloud Security", description: "Cloud environment security controls", icon: <CloudIcon /> },
    { value: "OT Security", description: "Operational Technology security controls", icon: <FactoryIcon /> },
    { value: "AI Security", description: "Artificial Intelligence security controls", icon: <SmartToyIcon /> },
    { value: "ISO 27001 Combined", description: "Combined ISO 27001 framework", icon: <VerifiedIcon /> },
    { value: "Data Protection Essential (DPE) Combined", description: "Combined Data Protection Essential framework", icon: <ShieldIcon /> },
    { value: "CTM Singapore Standards 712 (SS 712) Combined", description: "Combined Singapore Standards 712 framework", icon: <PolicyIcon /> }
  ],
  "E8": [
    { value: "Level One", description: "Default - Basic maturity level", icon: <LooksOneIcon /> },
    { value: "Level Two", description: "Intermediate maturity level", icon: <LooksTwoIcon /> },
    { value: "Level Three", description: "Advanced maturity level", icon: <Looks3Icon /> },
    { value: "Non-corporate Commonwealth Entities (NCCEs)", description: "NCCE specific requirements", icon: <AccountBalanceIcon /> }
  ],
  "NIST-2": [
    { value: "Tier 1 - Partial", description: "Partial implementation of security practices", icon: <TierIcon tier={1} /> },
    { value: "Tier 2 - Risk-Informed", description: "Risk-informed security practices", icon: <TierIcon tier={2} /> },
    { value: "Tier 3 - Repeatable", description: "Repeatable security processes", icon: <TierIcon tier={3} /> },
    { value: "Tier 4 - Adaptive", description: "Adaptive security program", icon: <TierIcon tier={4} /> }
  ]
};

// Future-Fit framework codes (name_acronym / Framework "Code"):
//   FF7:2.3  — Future-Fit 7 Essentials
//   FFBB:2.3 — Future-Fit Business Benchmark
// Also accepts any FF:* / FF7* / FFBB* code so a later rename still resolves.
export const isFutureFitAcronym = (acronym) => /^FF/i.test(String(acronym || '').trim());

export const isFutureFitFramework = (framework) =>
  isFutureFitAcronym(framework?.name_acronym) ||
  String(framework?.name || '').toLowerCase().includes('future-fit');

const ffCategory = (code, title, description) => ({
  value: `${code} ${title}`,
  description,
  icon: <CategoryOutlinedIcon />,
  group: 'category',
});

// 23 Break-Even Goals — Category on each Future-Fit control is "BEnn <short title>".
export const FUTURE_FIT_CATEGORIES_FFBB = [
  ffCategory('BE01', 'Renewable Energy', 'Energy is from renewable sources'),
  ffCategory('BE02', 'Water', 'Water use is environmentally responsible and socially equitable'),
  ffCategory('BE03', 'Natural Resources', 'Natural resources are managed to respect the welfare of ecosystems, people and animals'),
  ffCategory('BE04', 'Procurement', 'Procurement safeguards the pursuit of future-fitness'),
  ffCategory('BE05', 'Operational Emissions', 'Operational emissions do not harm people or the environment'),
  ffCategory('BE06', 'Operational GHGs', 'Operations emit no greenhouse gases'),
  ffCategory('BE07', 'Operational Waste', 'Operational waste is eliminated'),
  ffCategory('BE08', 'Operational Encroachment', 'Operations do not encroach on ecosystems or communities'),
  ffCategory('BE09', 'Community Health', 'Community health is safeguarded'),
  ffCategory('BE10', 'Employee Health', 'Employee health is safeguarded'),
  ffCategory('BE11', 'Living Wage', 'Employees are paid at least a living wage'),
  ffCategory('BE12', 'Employee Terms', 'Employees are subject to fair employment terms'),
  ffCategory('BE13', 'Employee Discrimination', 'Employees are not subject to discrimination'),
  ffCategory('BE14', 'Employee Concerns', 'Employee concerns are actively solicited, impartially judged and transparently addressed'),
  ffCategory('BE15', 'Product Communications', 'Product communications are honest, ethical, and promote responsible use'),
  ffCategory('BE16', 'Product Concerns', 'Product concerns are actively solicited, impartially judged and transparently addressed'),
  ffCategory('BE17', 'Product Harm', 'Products do not harm people or the environment'),
  ffCategory('BE18', 'Product GHGs', 'Products emit no greenhouse gases'),
  ffCategory('BE19', 'Products Repurposed', 'Products can be repurposed'),
  ffCategory('BE20', 'Business Ethics', 'Business is conducted ethically'),
  ffCategory('BE21', 'Tax', 'The right tax is paid in the right place at the right time'),
  ffCategory('BE22', 'Lobbying', 'Lobbying and advocacy safeguard the pursuit of future-fitness'),
  ffCategory('BE23', 'Financial Assets', 'Financial assets safeguard the pursuit of future-fitness'),
];

const FF7_CODES = new Set(['BE01', 'BE06', 'BE07', 'BE10', 'BE11', 'BE20', 'BE23']);

export const FUTURE_FIT_CATEGORIES_FF7 = FUTURE_FIT_CATEGORIES_FFBB.filter((opt) =>
  FF7_CODES.has(opt.value.slice(0, 4))
);

export const getFutureFitCategoryOptions = (framework) => {
  const acronym = String(framework?.name_acronym || '');
  const name = String(framework?.name || '').toLowerCase();
  if (/^FF7/i.test(acronym) || name.includes('7 essentials')) {
    return FUTURE_FIT_CATEGORIES_FF7;
  }
  if (isFutureFitFramework(framework)) {
    return FUTURE_FIT_CATEGORIES_FFBB;
  }
  return [];
};

export const splitCoverageOptions = (options = []) => {
  const coverage = [];
  const categories = [];
  (Array.isArray(options) ? options : []).forEach((opt) => {
    if (opt && typeof opt === 'object' && opt.group === 'category') {
      categories.push(opt);
    } else {
      coverage.push(opt);
    }
  });
  return { coverage, categories };
};

export const normalizeFutureFitCategory = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(BE\d{2})\s*(.*)$/i);
  if (match) {
    return {
      code: match[1].toUpperCase(),
      name: match[2].trim().toLowerCase(),
      raw: raw.toLowerCase(),
    };
  }
  return { code: null, name: raw.toLowerCase(), raw: raw.toLowerCase() };
};

export const futureFitCategoriesMatch = (left, right) => {
  const a = normalizeFutureFitCategory(left);
  const b = normalizeFutureFitCategory(right);
  if (a.raw && a.raw === b.raw) return true;
  if (a.code && b.code && a.code === b.code) return true;
  if (a.name && b.name && a.name === b.name) return true;
  return false;
};

// Default coverage options when framework doesn't match any specific mapping
export const defaultCoverageOptions = [
  { value: "Cloud Security", description: "Implementation focusing on cloud infrastructure and services", icon: <CloudIcon /> },
  { value: "Artificial intelligence (AI)", description: "Implementation with emphasis on AI systems and algorithms", icon: <SmartToyIcon /> },
  { value: "Application (iOS / Android)", description: "Mobile application security controls and implementation", icon: <PhoneIphoneIcon /> },
  { value: "On Premise", description: "Traditional on-premises infrastructure security implementation", icon: <StorageIcon /> }
];

// Groups a flat GetFrameworkIndustryTypeTags() response into
// { [industry_type]: tag[] } for cheap lookup by getCoverageOptionsForFramework.
export const groupTagsByIndustryType = (tags = []) => {
  const map = {};
  (Array.isArray(tags) ? tags : []).forEach((tag) => {
    const industryType = tag?.industry_type;
    if (!industryType) return;
    if (!map[industryType]) map[industryType] = [];
    map[industryType].push(tag);
  });
  return map;
};

// Resolves the coverage/tag options to show as Create/Edit Project coverage
// cards for a given framework. Resolution order:
//   1. Explicit acronym mapping (coverageOptionsByFramework) always wins —
//      preserves existing behavior for CEM/CTM/ISO27001/E8/NIST-2 etc.
//   2. Framework Industry Type Tag catalog — e.g. Future-Fit / Sustainability
//      frameworks with no hardcoded acronym mapping show their SDG tags.
//   3. Generic cyber security defaultCoverageOptions.
// `tagsByIndustryType` should come from groupTagsByIndustryType(GetFrameworkIndustryTypeTags()).
export const getCoverageOptionsForFramework = (framework, tagsByIndustryType = {}) => {
  const acronym = framework?.name_acronym;
  let options;
  if (acronym && coverageOptionsByFramework[acronym]) {
    options = coverageOptionsByFramework[acronym];
  } else {
    const industryTypes = parseJsonArray(framework?.framework_industry_type);
    options = defaultCoverageOptions;
    for (const industryType of industryTypes) {
      const tags = tagsByIndustryType?.[industryType];
      if (Array.isArray(tags) && tags.length > 0) {
        options = tags
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name)))
          .map((tag) => ({
            value: tag.name,
            description: tag.description || '',
            icon: <PublicIcon />,
            group: 'sdg',
          }));
        break;
      }
    }
  }

  // Future-Fit (FF7:2.3 / FFBB:2.3 / any FF:* code): keep SDG coverage
  // cards and also expose Break-Even Goal categories as a second selection.
  const categories = getFutureFitCategoryOptions(framework);
  if (categories.length > 0) {
    return [...options, ...categories];
  }
  return options;
};

// Get color for a coverage option based on its category
export const getCoverageColor = (optionValue) => {
  if (!optionValue) return "#1976d2";
  
  const value = String(optionValue);
  const ctmTier = normalizeCtmTier(value);

  if (ctmTier) return TIER_COLORS[`TIER${ctmTier.tierNumber}`];
  
  if (value.includes("Cloud")) return "#2196f3";
  if (value.includes("AI")) return "#9c27b0";
  if (value.includes("Medical") || value.includes("Health")) return "#4caf50";
  if (value.includes("Application") || value.includes("iOS") || value.includes("Android")) return "#ff9800";
  if (value.includes("Premise")) return "#795548";
  if (value.includes("OT") || value.includes("Operation Technology")) return "#f44336";
  if (value.includes("Classical") || value === "Default") return "#607d8b";
  if (value.includes("ICT Vendor")) return "#00838f";
  if (value.includes("CMS Vendor")) return "#4caf50";
  if (value.includes("DPE") || value.includes("Data Protection")) return "#7b1fa2";
  if (value.includes("ISO 27001")) return "#1565c0";
  if (value.includes("SS 712") || value.includes("Singapore Standards")) return "#c62828";
  if (value.includes("NCCEs") || value.includes("Non-corporate Commonwealth")) return "#5d4037";
  if (value.includes("Level One")) return TIER_COLORS.TIER1;
  if (value.includes("Level Two")) return TIER_COLORS.TIER2;
  if (value.includes("Level Three")) return TIER_COLORS.TIER3;
  if (value.startsWith("SDG")) return "#2e7d32"; // UN SDG green
  if (/^BE\d{2}/i.test(value)) return "#1565c0"; // Future-Fit Break-Even Goal category

  return "#1976d2";
};

// Get icon for a coverage option
export const getCoverageIcon = (optionValue) => {
  if (!optionValue) return <SecurityIcon />;
  
  const value = String(optionValue);
  const ctmTier = normalizeCtmTier(value);
  
  // Special handling for tier-based options
  if (ctmTier) return <TierIcon tier={ctmTier.tierNumber} />;
  
  // E8 Level-based options
  if (value.includes("Level One")) return <LooksOneIcon />;
  if (value.includes("Level Two")) return <LooksTwoIcon />;
  if (value.includes("Level Three")) return <Looks3Icon />;
  
  // First try to find in all framework options
  for (const framework in coverageOptionsByFramework) {
    const found = coverageOptionsByFramework[framework].find(
      opt => (typeof opt === 'object' ? opt.value : opt) === optionValue
    );
    if (found && found.icon) return found.icon;
  }
  
  // Then check default options
  const defaultOption = defaultCoverageOptions.find(
    opt => (typeof opt === 'object' ? opt.value : opt) === optionValue
  );
  if (defaultOption && defaultOption.icon) return defaultOption.icon;
  
  // Special cases for icons not covered by the mapping
  if (value.includes("OT") || value.includes("Operation Technology")) return <FactoryIcon />;
  if (value.includes("Industrial") || value.includes("ICS")) return <PrecisionManufacturingIcon />;
  if (value.includes("SCADA")) return <ElectricalServicesIcon />;
  if (value.includes("ICT Vendor") || value.includes("CMS Vendor")) return <BusinessIcon />;
  if (value.includes("DPE") || value.includes("Data Protection")) return <ShieldIcon />;
  if (value.includes("ISO 27001")) return <VerifiedIcon />;
  if (value.includes("SS 712") || value.includes("Singapore Standards")) return <PolicyIcon />;
  if (value.includes("NCCEs") || value.includes("Non-corporate Commonwealth")) return <AccountBalanceIcon />;
  if (value === "Default" || value.includes("Classical")) return <ClassIcon />;
  if (value.startsWith("SDG")) return <PublicIcon />;
  if (/^BE\d{2}/i.test(value)) return <CategoryOutlinedIcon />;

  return <SecurityIcon />;
};

// Date formatting utility
export const formatDate = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return null;
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}/${month}/${day}`;
};

// Convert date string to ISO format for API
export const toISODateString = (dateStr) => {
  if (!dateStr) return null;
  return dateStr.split("/").join("-") + "T00:00:00Z";
};

// Distribute a start→end span across the 5 implementation stages using the
// standard 20/30/20/10/20 weighting (mirrors DialogProjectCreate's auto-calc).
// Returns { stage1Start, stage1End, ... stage5End } as YYYY/MM/DD strings
// (formatDate output — pass through toISODateString before sending to the API),
// or null when the span is invalid or shorter than 5 days.
export const computeImplementationStages = (startDate, endDate) => {
  if (!(startDate instanceof Date) || isNaN(startDate.getTime())) return null;
  if (!(endDate instanceof Date) || isNaN(endDate.getTime())) return null;

  const totalDays = differenceInDays(endDate, startDate) + 1;
  if (totalDays < 5) return null;

  const percentages = [0.20, 0.30, 0.20, 0.10, 0.20];
  const stages = {};
  let currentStart = startDate;
  percentages.forEach((pct, index) => {
    const stageDays = Math.max(1, Math.round(totalDays * pct));
    const stageEnd = addDays(currentStart, stageDays - 1);
    stages[`stage${index + 1}Start`] = formatDate(currentStart);
    stages[`stage${index + 1}End`] = formatDate(stageEnd);
    currentStart = addDays(stageEnd, 1);
  });
  return stages;
};

// Project status options
export const projectStatusOptions = [
  "Basic Setup",
  "Implementation",
  "Review",
  "Remediation",
  "Audit & Signoff",
  "Monitoring",
  "Maintenance",
  "Evaluation",
  "Optimization",
  "Renewal"
];

// Estimated duration options
export const estimatedDurationOptions = [
  "3 months",
  "6 months",
  "9 months",
  "12 months"
];

// Audit/Signoff dropdown options
export const yesNoOptions = ["Yes", "No"];

// Implementation stages (1-5)
export const getImplementationStages = (dates, setDates) => [
  { 
    name: "Stage 1 - Basic Setup", 
    startDate: dates.stage1Start, 
    endDate: dates.stage1End, 
    setStartDate: setDates.setStage1Start, 
    setEndDate: setDates.setStage1End, 
    description: "Initial setup and configuration",
    commentKey: "stage1_comment"
  },
  { 
    name: "Stage 2 - Implementation", 
    startDate: dates.stage2Start, 
    endDate: dates.stage2End, 
    setStartDate: setDates.setStage2Start, 
    setEndDate: setDates.setStage2End, 
    description: "Core implementation phase",
    commentKey: "stage2_comment"
  },
  { 
    name: "Stage 3 - Review", 
    startDate: dates.stage3Start, 
    endDate: dates.stage3End, 
    setStartDate: setDates.setStage3Start, 
    setEndDate: setDates.setStage3End, 
    description: "Review and assessment",
    commentKey: "stage3_comment"
  },
  { 
    name: "Stage 4 - Remediation", 
    startDate: dates.stage4Start, 
    endDate: dates.stage4End, 
    setStartDate: setDates.setStage4Start, 
    setEndDate: setDates.setStage4End, 
    description: "Address identified issues",
    commentKey: "stage4_comment"
  },
  { 
    name: "Stage 5 - Audit & Signoff", 
    startDate: dates.stage5Start, 
    endDate: dates.stage5End, 
    setStartDate: setDates.setStage5Start, 
    setEndDate: setDates.setStage5End, 
    description: "Final audit and approval",
    commentKey: "stage5_comment"
  }
];

// Consultant stages (6-10)
export const getConsultantStages = (dates, setDates, stageDoneStatus) => [
  { 
    name: "Stage 6 - Monitoring", 
    startDate: dates.stage6Start, 
    endDate: dates.stage6End, 
    setStartDate: setDates.setStage6Start, 
    setEndDate: setDates.setStage6End, 
    description: "Ongoing monitoring phase",
    isDone: stageDoneStatus?.is_stage6_done,
    commentKey: "stage6_comment"
  },
  { 
    name: "Stage 7 - Maintenance", 
    startDate: dates.stage7Start, 
    endDate: dates.stage7End, 
    setStartDate: setDates.setStage7Start, 
    setEndDate: setDates.setStage7End, 
    description: "System maintenance",
    isDone: stageDoneStatus?.is_stage7_done,
    commentKey: "stage7_comment"
  },
  { 
    name: "Stage 8 - Evaluation", 
    startDate: dates.stage8Start, 
    endDate: dates.stage8End, 
    setStartDate: setDates.setStage8Start, 
    setEndDate: setDates.setStage8End, 
    description: "Performance evaluation",
    isDone: stageDoneStatus?.is_stage8_done,
    commentKey: "stage8_comment"
  },
  { 
    name: "Stage 9 - Optimization", 
    startDate: dates.stage9Start, 
    endDate: dates.stage9End, 
    setStartDate: setDates.setStage9Start, 
    setEndDate: setDates.setStage9End, 
    description: "Process optimization",
    isDone: stageDoneStatus?.is_stage9_done,
    commentKey: "stage9_comment"
  },
  { 
    name: "Stage 10 - Renewal", 
    startDate: dates.stage10Start, 
    endDate: dates.stage10End, 
    setStartDate: setDates.setStage10Start, 
    setEndDate: setDates.setStage10End, 
    description: "Certification renewal",
    isDone: stageDoneStatus?.is_stage10_done,
    commentKey: "stage10_comment"
  }
];

// User roles that can view consultant stages
export const consultantViewRoles = [
  'Super Admin - Admin',
  'Tech Admin - Admin',
  'Tech Admin - Member',
  'Consultant - Admin',
  'Consultant - Member'
];

// Check if user can view consultant stages
export const canViewConsultantStages = () => {
  const userType = localStorage.getItem("userType");
  return consultantViewRoles.includes(userType);
};

